import { isNull } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { cn } from '@/shared/lib'
import { Button, Dialog } from '@/shared/ui'

import { DIALOG } from '../../../lib/contract-dialog'
import { useContract } from '../contract-information/provider'

type DialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export const CheckExtendContractDialog = ({
  isOpen,
  onOpenChange,
}: DialogProps) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content className={cn('h-[347px] gap-0 p-0')}>
        <ExtendContractDialogContent />
      </Dialog.Content>
    </Dialog.Root>
  )
}

export const ExtendContractDialogContent = () => {
  const t = useTranslations('extend_lease')

  const [extend, setExtend] = useState<boolean | null>(null)

  const { openDialog } = useContract()

  const handleCheckBoxClick = (value: boolean) => {
    setExtend(value)
  }

  const handleRequestButtonClick = () => {
    if (isNull(extend)) {
      return
    }

    if (extend) {
      openDialog(DIALOG.END_DATE_CALENDAR, { closeOnOpen: true })
    } else {
      openDialog(DIALOG.PERIOD_CALENDAR, { closeOnOpen: true })
    }
  }

  return (
    <>
      <Dialog.Header className="p-6 pb-4">
        <Dialog.Title className="text-start">{t('modal.title')}</Dialog.Title>
      </Dialog.Header>

      <div className="space-y-6 px-6">
        <div className="typo-label1 h-11 font-normal whitespace-break-spaces text-gray-900">
          {t.rich('modal.subtitle', {
            strong: chunks => <span className="text-purple-500">{chunks}</span>,
          })}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <CheckBox
            title={t('modal.option.new_lease.title')}
            description={t('modal.option.new_lease.description')}
            value={false}
            selected={extend === false}
            onClick={handleCheckBoxClick}
          />
          <CheckBox
            title={t('modal.option.extend_lease.title')}
            description={t('modal.option.extend_lease.description')}
            value={true}
            selected={extend === true}
            onClick={handleCheckBoxClick}
          />
        </div>
      </div>

      <Dialog.Footer className="p-6">
        <Button
          type="button"
          variant="solid"
          color="primary"
          size="large"
          fullWidth
          disabled={isNull(extend)}
          onClick={handleRequestButtonClick}
        >
          {t('modal.button')}
        </Button>
      </Dialog.Footer>
    </>
  )
}

type CheckBoxProps = {
  title: string
  description: string
  value: boolean
  selected: boolean
  onClick: (value: boolean) => void
}

export const CheckBox = ({
  title,
  description,
  value,
  selected,
  onClick,
}: CheckBoxProps) => {
  const handleClick = () => {
    onClick(value)
  }

  return (
    <button
      className={cn(
        'flex h-[116px] grow cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-transparent bg-gray-50 p-6',
        'transition-colors hover:bg-gray-100',
        {
          'border-black bg-purple-50 hover:bg-purple-50': selected,
        },
      )}
      onClick={handleClick}
    >
      <p className="typo-label2 font-normal whitespace-break-spaces text-gray-500">
        {description}
      </p>
      <p className="typo-body1 font-medium text-black">{title}</p>
    </button>
  )
}
